<template>
    <div class="container-fluid">
        <Breadcrumb title="PMTA Relays Indicators" v-bind:button-show="false"></Breadcrumb>
        <div class="row" style="position: relative">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        This report is generated once a day at night. <br/>
                        For relays older than 1 year, the calculation is based on data for the last year.
                        <button type="button" class="close" v-tooltip.top="'Download xlsx'">
                            <i class="fas fa-file-excel mr-1" aria-hidden="true" @click="getXlsx()"
                                style="font-size: 20px;"></i>
                        </button>
                    </div>
                    <div class="card-body">
                        <div class="input-group form-group">
                            <div class="form-group">
                                <label>Relay Type</label>
                                <treeselect v-model="filter.isVps" placeholder="Type" :clearable=false :multiple="false"
                                    :options="relayTypeSelect" />
                            </div>
                            <div class="form-group">
                                <label>Relay Group</label>
                                <treeselect v-model="filter.relayGroupId" placeholder="All Relays" :multiple="false"
                                    :options="relayGroupList" :load-options="loadRelayGroupList"
                                    :auto-load-root-options="true" />
                            </div>
                            <div class="form-group">
                                <label>Mail Service</label>
                                <treeselect v-model="filter.mailProviderId" placeholder="Select Mail Service"
                                    :multiple="false" :options="mailProviderList" :load-options="loadMailProviderList"
                                    :auto-load-root-options="true" />
                            </div>
                            <input v-model="filter.smtpHost" type="text" class="form-control form-control-line"
                                placeholder="Search by SMTP Host">
                        </div>
                        <div class="form-row">
                            <div class="col-2">
                                <button class="btn btn-info btn-rounded text-uppercase waves-effect waves-light w-100"
                                    @click="getItems(page = 1)">
                                    Get
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="col-12" v-if="loading" style="min-height:100px;">
                            <Preloader v-bind:class="{ preloader: loading }"></Preloader>
                        </div>
                        <div v-else>
                            <div v-if="report.length === 0">{{ emptyMessage }}</div>
                            <div v-else>
                                <div>
                                    <div class="table-responsive">
                                        <table class="table">
                                            <thead>
                                                <tr>
                                                    <th @click="tableOrdering('pmtaRelay.id')">
                                                        <span role='button'>Relay ID</span>
                                                    </th>
                                                    <th @click="tableOrdering('pmtaRelay.pmtaServer.isVps')">
                                                        <span role='button'>Type</span>
                                                    </th>
                                                    <th @click="tableOrdering('pmtaRelay.group.name')">
                                                        <span role='button'>Group</span>
                                                    </th>
                                                    <th @click="tableOrdering('pmtaRelay.smtpHost')">
                                                        <span role='button'>Host</span>
                                                    </th>
                                                    <th @click="tableOrdering('pmtaRelay.ip')">
                                                        <span role='button'>IP</span>
                                                    </th>
                                                    <th @click="tableOrdering('age', 'desc')">
                                                        <span role='button'>Age</span>
                                                    </th>
                                                    <th @click="tableOrdering('deliveredPerDay', 'desc')">
                                                        <span role='button'>Delivered<br/>per day</span>
                                                    </th>
                                                    <th @click="tableOrdering('bouncedPerDay', 'desc')">
                                                        <span role='button'>Bounced<br/>per day</span>
                                                    </th>
                                                    <th @click="tableOrdering('conversionPerDay', 'desc')">
                                                        <span role='button'>Conversions<br/>per day</span>
                                                    </th>
                                                    <th @click="tableOrdering('profit90', 'desc')">
                                                        <span role='button'>Profit 90</span>
                                                    </th>
                                                    <th @click="tableOrdering('profit60', 'desc')">
                                                        <span role='button'>Profit 60</span>
                                                    </th>
                                                    <th @click="tableOrdering('profit30', 'desc')">
                                                        <span role='button'>Profit 30</span>
                                                    </th>
                                                    <th @click="tableOrdering('profitPerDay', 'desc')">
                                                        <span role='button'>Profit<br/>per day</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(indicator, index) in report" :key="index" 
                                                    :class="{ 'bg-rose': ((indicator.pmtaRelay.status === 3) || (indicator.pmtaRelay.active === false)) }">
                                                    <td class="align-middle">
                                                        <router-link
                                                            :to="{ name: 'pmtaRelayFilter3', params: { relayId: indicator.pmtaRelay.id } }">
                                                            {{ indicator.pmtaRelay.id }}
                                                        </router-link>
                                                    </td>
                                                    <td class="align-middle">{{ indicator.pmtaRelay.pmtaServer.isVps ?
                                                        'VPS' : 'Net'}}</td>
                                                    <td class="align-middle">{{ indicator.pmtaRelay.group.name }}</td>
                                                    <td class="align-middle">{{ indicator.pmtaRelay.smtpHost }}</td>
                                                    <td class="align-middle">{{ indicator.pmtaRelay.ip }}</td>
                                                    <td class="align-middle">
                                                        <span v-tooltip.top="'Age'">{{ indicator.age }}</span>
                                                    </td>
                                                    <td class="align-middle">
                                                        <span
                                                            v-tooltip.top="'Delivered'">{{ indicator.deliveredPerDay }}</span>
                                                    </td>
                                                    <td class="align-middle">
                                                        <span
                                                            v-tooltip.top="'Bounced'">{{ indicator.bouncedPerDay }}</span>
                                                    </td>
                                                    <td class="align-middle">
                                                        <span
                                                            v-tooltip.top="'Conversions'">{{ indicator.conversionPerDay }}</span>
                                                    </td>
                                                    <td class="align-middle">
                                                        <span v-tooltip.top="'Profit 90'">
                                                            {{ indicator.profit90 }} ({{ indicator.conv90 }})
                                                        </span>
                                                    </td>
                                                    <td class="align-middle">
                                                        <span v-tooltip.top="'Profit 60'">
                                                            {{ indicator.profit60 }} ({{ indicator.conv60 }})
                                                        </span>
                                                    </td>
                                                    <td class="align-middle">
                                                        <span v-tooltip.top="'Profit 30'">
                                                            {{ indicator.profit30 }} ({{ indicator.conv30 }})
                                                        </span>
                                                    </td>
                                                    <td class="align-middle"
                                                        v-bind:class="{ 'bg-warning': indicator.profitPerDay <= '0.60000' }">
                                                        <span v-tooltip.top="'Profit'">{{ indicator.profitPerDay }}</span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <paginate :click-handler="getItems"
                                        :container-class="'pagination justify-content-center'" :margin-pages="2"
                                        :next-class="'page-item'" :next-link-class="'page-link'" :next-text="'Next'"
                                        :page-class="'page-item'" :page-count="pageCount" :page-link-class="'page-link'"
                                        :page-range="3" :prev-class="'page-item'" :prev-link-class="'page-link'"
                                        :prev-text="'Prev'" v-if="pageCount > 1" v-model="page">
                                    </paginate>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import Swal from 'sweetalert2'
import Preloader from '../components/Preloader';
import Breadcrumb from '../components/Breadcrumb';
import ReportService from '../services/ReportService';
import MailProviderService from '../services/MailProviderService';
import PmtaRelayGroupService from '../services/PmtaRelayGroupService';
import paginateMixin from '../mixins/paginate-mixin';

export default {
    name: 'PmtaRelayIndicatorReport',
    components: {
        Treeselect,
        Breadcrumb,
        Preloader,
    },
    mixins: [paginateMixin],
    data() {
        return {
            mailProviderList: null,
            emptyMessage: '',
            totalItems: 0,
            page: 1,
            perPage: paginateMixin.defaultPerPage,
            report: [],
            relayGroupList: null,
            filter: {
                relayGroupId: null,
                mailProviderId: undefined,
                smtpHost: null,
                isVps: 'All'
            },
            order: {
                field: 'id',
                direction: 'asc'
            },
            relayTypeSelect: [
                { id: 'All', label: 'All' },
                { id: true, label: 'VPS' },
                { id: false, label: 'Net' }
            ],
            loading: false,
            isAdmin: false
        }
    },
    mounted() {
        this.isAdmin = this.$store.getters.isAdmin;
    },
    methods: {
        loadRelayGroupList({ callback }) {
            PmtaRelayGroupService.getGroupSelect().then(response => {
                this.relayGroupList = response.data['hydra:member'].map(group => {
                    return { id: group.id, label: group.name }
                });
                callback();
            });
        },
        loadMailProviderList({ callback }) {
            MailProviderService.getProviderSelect().then(response => {
                this.mailProviderList = response.data['hydra:member'].map(group => {
                    return { id: group.id, label: group.name }
                });
                callback();
            });
        },
        getItems: function (pageNum) {
            this.loading = true;
            if (this.filter.isVps === 'All') {
                this.filter.isVps = null;
            }
            ReportService.getPmtaRelayIndicatorReport(
                pageNum,
                this.perPage,
                this.order,
                this.filter
            ).then(response => {
                this.report = response.data['hydra:member'];
                this.totalItems = response.data['hydra:totalItems'];
                if (this.totalItems === 0) {
                    this.emptyMessage = 'Nothing to show.'
                } else {
                    this.emptyMessage = ''
                }
                this.loading = false;
            }).catch(error => {
                Swal({
                    type: 'error',
                    title: 'Oops...',
                    text: `${error}`,
                });
                this.loading = false;
            });
        },
        tableOrdering(field, direction) {
            if (this.order.field === field) {
                this.order.direction = (this.order.direction === 'asc') ? 'desc' : 'asc';
            } else {
                this.order.field = field;
                this.order.direction = direction ? direction : 'asc';
            }
            this.getItems(this.page = 1);
        },
        getXlsx() {
            if (this.filter.smtpHost) {
                Swal({
                    type: 'error',
                    title: 'Filter is not supported.',
                    text: 'Clean "SMTP Host" field.',
                });
                return;
            }
            ReportService.getXlsxPmtaRelayIndicatorReport(JSON.stringify(this.filter)).then(response => {
                const blob = new Blob([response.data]);
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'pmta-relays-indicators-report.xlsx';
                link.click();
                URL.revokeObjectURL(link.href);
            });
        },
    },
    computed: {
        pageCount: function () {
            return Math.ceil(this.totalItems / this.perPage);
        }
    }
}
</script>

<style scoped>
tbody {
    font-size: 14px;
    font-family: Arial, serif;
}
.bg-rose {
    background-color: #ffebe9;
}
</style>
